import React, { useCallback, useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import { LTDrawer } from '@lambdatestincprivate/lt-components';
import { Portal } from '@primer/react';
import RCABanner from './RCABanner';
import RCACompactTrigger from './RCACompactTrigger';
import RCARowTrigger from './RCARowTrigger';
import RCADrawerContent from './RCADrawerContent';
import useRCA from './useRCA';
import { CreditSDK } from '@lambdatestincprivate/credit-sdk';
import { getProductRCA, isRCASupportedFramework } from './helpers';
import { FAILURE_STATUSES } from './constants';

const AIRca = forwardRef(({
  testId,
  status,
  testName,
  subTitle,
  createdAt,
  rcaCategory,
  frameworkType,
  rawType,
  apiBaseUrl,
  userId,
  cookieName,
  product: productProp,
  loading,
  onDrawerClose,
  onTriggerClick,
  extraQueryParams,
  extraAnalyticsParams,
  triggerIcon,
  showGenerate,
  externalError,
  onClearExternalError,
  bannerMode,
  aiCapabilityEnabled = true,
  baseUrl,
  totalTests,
  activeTestIndex,
  onTestNavigate,
  onViewFailedStep,
}, ref) => {
  const drawerRef = useRef(null);
  const pendingOpenRef = useRef(false);

  const isHyperExecute = productProp === AIRca.Products.HYPEREXECUTE;
  const isTestManager = productProp === AIRca.Products.TEST_MANAGER;
  const isFailedTest = isHyperExecute || isTestManager || FAILURE_STATUSES.includes(status);
  const isSupported =
    isHyperExecute || isTestManager || isRCASupportedFramework(frameworkType, rawType);
  // TEST_MANAGER is a UI mode, not an API product — resolve actual product from testId
  const product = (isTestManager || !productProp) ? getProductRCA({ testId }) : productProp;

  const {
    rcaData,
    isLoading,
    isError,
    errorMessage,
    isNotEnoughCredit,
    dismissNotEnoughCredit,
    errorRepetitionData,
    errorTrendsData,
    triggerRCA,
    fetchExistingRCA,
    refetch,
    submitFeedback,
  } = useRCA({
    testId,
    product,
    apiBaseUrl,
    userId,
    testName,
    createdAt,
    rcaCategory,
    cookieName,
    extraQueryParams,
    extraAnalyticsParams,
    isTestManager,
    enabled: !isHyperExecute && !isTestManager && isFailedTest && isSupported && !!testId,
  });

  // Deferred open — when testId resolves after trigger click
  // Checks for existing RCA so user can generate if needed.
  useEffect(() => {
    if (pendingOpenRef.current && testId) {
      pendingOpenRef.current = false;
      drawerRef.current?.openDrawer();
      fetchExistingRCA();
    }
  }, [testId, fetchExistingRCA]);

  // Auto-fetch when testId changes during navigation (HyperExecute/TestManager)
  const prevTestIdRef = useRef(testId);
  useEffect(() => {
    const needToFetch =
      (isHyperExecute || isTestManager) &&
      testId &&
      prevTestIdRef.current &&
      testId !== prevTestIdRef.current;

    if (needToFetch) {
      fetchExistingRCA();
    }
    prevTestIdRef.current = testId;
  }, [testId, isHyperExecute, isTestManager, fetchExistingRCA]);

  const handleViewRCA = useCallback(() => {
    drawerRef.current?.openDrawer();
    // Fetch existing RCA if not already loaded (e.g. TEST_MANAGER where enabled=false)
    if (!rcaData) {
      fetchExistingRCA();
    }
  }, [fetchExistingRCA, rcaData]);

  const handleGenerateRCA = useCallback(() => {
    drawerRef.current?.openDrawer();
    triggerRCA();
  }, [triggerRCA]);

  const handleClose = useCallback(() => {
    drawerRef.current?.closeDrawer();
    onClearExternalError?.();
    onDrawerClose?.();
  }, [onDrawerClose, onClearExternalError]);

  const handleTriggerClick = useCallback(
    (e) => {
      e?.stopPropagation?.();
      onTriggerClick?.();
      if (testId) {
        drawerRef.current?.openDrawer();
        if (isHyperExecute) {
          fetchExistingRCA();
        } else {
          triggerRCA();
        }
      } else {
        pendingOpenRef.current = true;
      }
    },
    [testId, onTriggerClick, isHyperExecute, fetchExistingRCA, triggerRCA]
  );

  const handleDismissCredit = useCallback(() => {
    dismissNotEnoughCredit();
  }, [dismissNotEnoughCredit]);

  // Close drawer when credits are insufficient — only show the modal
  useEffect(() => {
    if (isNotEnoughCredit) {
      drawerRef.current?.closeDrawer();
    }
  }, [isNotEnoughCredit]);

  // Open drawer when external error is set (e.g. Test ID Not Found)
  useEffect(() => {
    if (externalError) {
      drawerRef.current?.openDrawer();
    }
  }, [externalError]);

  useImperativeHandle(ref, () => ({
    openDrawer: handleViewRCA,
  }), [handleViewRCA]);

  // Show loading while useRCA is fetching OR testId is still being resolved
  const combinedLoading = isLoading || (!!loading && !testId);

  // For HyperExecute/TestManager, allow rendering without testId (trigger still shows)
  if (
    (!isHyperExecute && !isTestManager && (!isFailedTest || !isSupported || !testId)) ||
    ((isHyperExecute || isTestManager) && !isFailedTest)
  ) {
    return null;
  }

  return (
    <>
      {isHyperExecute ? (
        <RCACompactTrigger
          rcaCategory={rcaCategory}
          icon={triggerIcon}
          onClick={handleTriggerClick}
        />
      ) : isTestManager && !bannerMode ? (
        <RCARowTrigger
          rcaCategory={rcaCategory}
          isLoading={combinedLoading}
          onGenerateRCA={handleGenerateRCA}
          onViewRCA={handleViewRCA}
          showGenerate={showGenerate && aiCapabilityEnabled}
        />
      ) : (
        <RCABanner
          rcaData={rcaData}
          rcaCategory={isTestManager ? rcaCategory : undefined}
          isLoading={combinedLoading}
          onViewRCA={handleViewRCA}
          onGenerateRCA={
            aiCapabilityEnabled && (!isTestManager || showGenerate) ? handleGenerateRCA : undefined
          }
          onViewFailedStep={onViewFailedStep}
        />
      )}
      <Portal>
      <LTDrawer
        ref={drawerRef}
        title=""
        width="800px"
        closable={false}
        noPortal
        position={LTDrawer.Position.RIGHT}
        content={
          <RCADrawerContent
            isLoading={combinedLoading}
            isError={isError || !!externalError}
            errorTitle={externalError ? 'Test ID Not Found' : undefined}
            errorMessage={externalError ? externalError : undefined}
            errorType={externalError && 'warning'}
            rcaData={rcaData}
            testName={testName}
            subTitle={subTitle}
            status={status}
            testId={testId}
            product={product}
            errorRepetitionData={errorRepetitionData}
            errorTrendsData={errorTrendsData}
            onClose={handleClose}
            onRetry={externalError ? undefined : triggerRCA}
            onRefetch={refetch}
            onGenerate={triggerRCA}
            submitFeedback={submitFeedback}
            rcaCategory={rcaCategory}
            baseUrl={baseUrl}
            totalTests={totalTests}
            activeTestIndex={activeTestIndex}
            onTestNavigate={onTestNavigate}
          />
        }
        onClose={handleClose}
        bodyStyle={{ padding: '0px' }}
        headerStyle={{ display: 'none' }}
      />
      {isNotEnoughCredit && (
          <CreditSDK open onClose={handleDismissCredit} />
      )}
      </Portal>
    </>
  );
});

AIRca.Products = {
  HYPEREXECUTE: 'hyperexecute',
  APP_AUTOMATION: 'app-automation',
  WEB_AUTOMATION: 'web-automation',
  TEST_MANAGER: 'test-manager',
};

export default AIRca;
